import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ChevronDown, Filter, Star, ShoppingCart, Check, X } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa'; // Import WhatsApp icon
import Input from '../components/common/Input';
import { useCart } from '../hooks/useCart';
import { formatPrice } from '../utils/helpers';
import { productService } from '../services/productService';
import type { FirebaseProduct } from '../types/product';

interface FilterState {
  category: string[];
  brand: string[];
  priceRange: [number, number];
  rating: number | null;
  gsm?: number[];
  size?: string[];
  finish?: string[];
}

// Constants for filtering
const CATEGORIES = [
  'Printing Paper',
  'Photo Paper',
  'Art Paper',
  'Specialty Paper',
  'Binding Supplies',
  'Office Stationery',
];

const BRANDS = [
  'Double A',
  'HP Papers',
  'Xerox',
  'Navigator',
  'Mondi',
  'PaperOne',
  'IK Plus',
];

const GSM_OPTIONS = [70, 80, 100, 120, 160, 200, 250];
const SIZE_OPTIONS = ['A4', 'A3', 'A5', 'Legal', 'Letter'];
const FINISH_OPTIONS = ['Matte', 'Glossy', 'Semi-Glossy', 'Pearl'];

const Products: React.FC = () => {
  const { addToCart } = useCart();
  const [products, setProducts] = useState<FirebaseProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    category: [],
    brand: [],
    priceRange: [0, 5000],
    rating: null,
    gsm: [],
    size: [],
    finish: [],
  });

  const [sortBy, setSortBy] = useState<string>('popularity');
  const [showFilters, setShowFilters] = useState(false);

  // Fetch products on component mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const productsData = await productService.getProducts();
        setProducts(productsData);
      } catch (err) {
        setError('Failed to load products. Please try again later.');
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const handleAddToCart = async (product: FirebaseProduct) => {
    try {
      const updatedProduct = await productService.getProduct(product.id);
      if (!updatedProduct || updatedProduct.stock === 0) {
        setError('Product is out of stock');
        return;
      }

      addToCart(product);
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3000);
    } catch (err) {
      setError('Failed to add product to cart');
    }
  };

  // Filter products based on current filter state
  const filteredProducts = products.filter((product) => {
    // Category filter
    if (filters.category.length && !filters.category.includes(product.category)) {
      return false;
    }
    
    // Brand filter
    if (filters.brand.length && !filters.brand.includes(product.brand)) {
      return false;
    }
    
    // Price range filter
    if ((product.price ?? 0) < filters.priceRange[0] || (product.price ?? 0) > filters.priceRange[1]) {
      return false;
    }
    
    // Rating filter
    if (filters.rating && product.rating && product.rating < filters.rating) {
      return false;
    }
    
    // GSM filter
    if (filters.gsm?.length && product.gsm) {
      if (!filters.gsm.includes(product.gsm)) {
        return false;
      }
    }
    
    // Size filter
    if (filters.size?.length && product.size) {
      if (!filters.size.includes(product.size)) {
        return false;
      }
    }
    
    // Finish filter
    if (filters.finish?.length && product.finish) {
      if (!filters.finish.includes(product.finish)) {
        return false;
      }
    }
    
    return true;
  });

  // Sort filtered products
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price_asc':
        return (a.price ?? 0) - (b.price ?? 0);
      
      case 'price_desc':
        return (b.price ?? 0) - (a.price ?? 0);
      
      case 'rating':
        return ((b.rating || 0) - (a.rating || 0));
      
      case 'newest':
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      
      case 'sheets_desc':
        return ((b.sheets || 0) - (a.sheets || 0));
      
      case 'gsm_desc':
        return ((b.gsm || 0) - (a.gsm || 0));
      
      default: // popularity
        return (b.reviewCount || 0) - (a.reviewCount || 0);
    }
  });

  // Handle loading state
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm p-4 animate-pulse">
              <div className="h-48 bg-gray-200 rounded-lg mb-4" />
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
              <div className="h-4 bg-gray-200 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="container mx-auto px-4 py-14 text-center">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          {error}
          <button 
            onClick={() => setError(null)}
            className="ml-4 underline hover:no-underline"
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Paper Products | Perfect Paper</title>
        <meta
          name="description"
          content="Browse our extensive collection of premium paper products, from printing paper to specialty materials."
        />
      </Helmet>

      {/* Success Toast */}
      {showSuccessToast && (
        <div className="fixed top-4 right-4 bg-green-500 text-black px-4 py-3 rounded-lg 
          shadow-lg flex items-center gap-2 animate-fade-in z-50">
          <Check className="h-5 w-5" />
          Item added to cart
        </div>
      )}

      <div className="container mx-auto px-4 py-14">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Paper Products</h1>
          <p className="text-gray-600">Find the perfect paper for your printing and stationery needs</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Filters - Desktop */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-semibold text-gray-900">Filters</h2>
                <button
                  onClick={() =>
                    setFilters({
                      category: [],
                      brand: [],
                      priceRange: [0, 5000],
                      rating: null,
                      gsm: [],
                      size: [],
                      finish: [],
                    })
                  }
                  className="text-sm text-brand-blue-500 hover:text-brand-blue-600"
                >
                  Reset All
                </button>
              </div>

              {/* Categories */}
              <div className="mb-6">
                <h3 className="font-medium mb-3">Category</h3>
                <div className="space-y-2">
                  {CATEGORIES.map((category) => (
                    <label key={category} className="flex items-center">
                      <input
                        type="checkbox"
                        className="form-checkbox text-brand-blue-500 rounded"
                        checked={filters.category.includes(category)}
                        onChange={(e) => {
                          setFilters((prev) => ({
                            ...prev,
                            category: e.target.checked
                              ? [...prev.category, category]
                              : prev.category.filter((c) => c !== category),
                          }));
                        }}
                      />
                      <span className="ml-2 text-gray-700">{category}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* GSM Range */}
              <div className="mb-6">
                <h3 className="font-medium mb-3">Paper Weight (GSM)</h3>
                <div className="space-y-2">
                  {GSM_OPTIONS.map((gsm) => (
                    <label key={gsm} className="flex items-center">
                      <input
                        type="checkbox"
                        className="form-checkbox text-brand-blue-500 rounded"
                        checked={filters.gsm?.includes(gsm)}
                        onChange={(e) => {
                          setFilters((prev) => ({
                            ...prev,
                            gsm: e.target.checked
                              ? [...(prev.gsm || []), gsm]
                              : (prev.gsm || []).filter((g) => g !== gsm),
                          }));
                        }}
                      />
                      <span className="ml-2 text-gray-700">{gsm} GSM</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Paper Size */}
              <div className="mb-6">
                <h3 className="font-medium mb-3">Paper Size</h3>
                <div className="space-y-2">
                  {SIZE_OPTIONS.map((size) => (
                    <label key={size} className="flex items-center">
                      <input
                        type="checkbox"
                        className="form-checkbox text-brand-blue-500 rounded"
                        checked={filters.size?.includes(size)}
                        onChange={(e) => {
                          setFilters((prev) => ({
                            ...prev,
                            size: e.target.checked
                              ? [...(prev.size || []), size]
                              : (prev.size || []).filter((s) => s !== size),
                          }));
                        }}
                      />
                      <span className="ml-2 text-gray-700">{size}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Price Range Inputs */}
              <div className="mb-6">
                <h3 className="font-medium mb-3">Price Range (KES)</h3>
                <div className="space-y-2">
                  <Input
                    type="number"
                    placeholder="Min Price"
                    value={filters.priceRange[0]}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        priceRange: [+e.target.value, prev.priceRange[1]],
                      }))
                    }
                  />
                  <Input
                    type="number"
                    placeholder="Max Price"
                    value={filters.priceRange[1]}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        priceRange: [prev.priceRange[0], +e.target.value],
                      }))
                    }
                  />
                </div>
              </div>

              {/* Rating Filter */}
              <div>
                <h3 className="font-medium mb-3">Rating</h3>
                <div className="space-y-2">
                  {[4, 3, 2].map((rating) => (
                    <label key={rating} className="flex items-center">
                      <input
                        type="radio"
                        className="form-radio text-brand-blue-500"
                        checked={filters.rating === rating}
                        onChange={() => setFilters((prev) => ({ ...prev, rating }))}
                      />
                      <span className="ml-2 flex items-center text-gray-700">
                        {rating}+ <Star className="h-4 w-4 ml-1 fill-yellow-400" />
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1">
            {/* Mobile Filter Button */}
            <div className="lg:hidden flex items-center justify-between mb-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border shadow-sm text-gray-700"
              >
                <Filter className="h-5 w-5" />
                Filters
              </button>

              <select
                className="px-4 py-2 bg-white rounded-lg border shadow-sm"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="popularity">Most Popular</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
                <option value="sheets_desc">Sheets per Pack</option>
                <option value="gsm_desc">Paper Weight</option>
              </select>
            </div>

            {/* Mobile Filters Modal */}
            {showFilters && (
              <div className="fixed inset-0 z-50 lg:hidden">
                <div className="absolute inset-0 bg-black/50" onClick={() => setShowFilters(false)} />
                <div className="absolute inset-y-0 right-0 w-full max-w-xs bg-white">
                  <div className="p-6 h-full overflow-y-auto">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="font-semibold text-gray-900">Filters</h2>
                      <button onClick={() => setShowFilters(false)}>
                        <X className="h-6 w-6 text-gray-400" />
                      </button>
                    </div>
                    {/* Mobile filters content - same as desktop */}
                  </div>
                </div>
              </div>
            )}

            {/* Sort Bar - Desktop */}
            <div className="hidden lg:flex items-center justify-between mb-6">
              <p className="text-gray-600">Showing {sortedProducts.length} results</p>
              <select
                className="px-4 py-2 bg-white rounded-lg border shadow-sm"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="popularity">Most Popular</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
                <option value="sheets_desc">Sheets per Pack</option>
                <option value="gsm_desc">Paper Weight</option>
              </select>
            </div>

            {/* Products Grid */}
            {sortedProducts.length === 0 ? (
              <div className="text-center py-14 bg-white rounded-lg shadow-sm">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                  <Filter className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-600 mb-4">Try adjusting your filters or search criteria</p>
                <button
                  onClick={() => setFilters({
                    category: [],
                    brand: [],
                    priceRange: [0, 5000],
                    rating: null,
                    gsm: [],
                    size: [],
                    finish: [],
                  })}
                  className="text-brand-blue-500 hover:text-brand-blue-600 font-medium"
                >
                  Clear all filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sortedProducts.map((product) => (
                  <div
                    key={product.id}
                    className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  >
                    <Link to={`/product/${product.slug}`}>
                      <div className="aspect-square bg-gray-50 rounded-t-lg relative overflow-hidden">
                        <img
                          src={product.images[0]?.url || '/api/placeholder/400/400'}
                          alt={product.images[0]?.alt || product.name}
                          className="w-full h-full object-contain p-4 hover:scale-105 transition-transform duration-300"
                        />
                        {product.stock < 25 && product.stock > 0 && (
                          <div className="absolute top-2 right-2 bg-yellow-500 text-black px-2 py-1 rounded text-xs">
                            Low Stock
                          </div>
                        )}
                        {product.stock === 0 && (
                          <div className="absolute top-2 right-2 bg-red-500 text-black px-2 py-1 rounded text-xs">
                            Out of Stock
                          </div>
                        )}
                      </div>
                    </Link>

                    <div className="p-4">
                      <Link to={`/product/${product.slug}`} className="block group">
                        <p className="text-sm text-brand-blue-500 mb-1">{product.brand}</p>
                        <h3 className="text-lg font-medium text-gray-900 group-hover:text-brand-blue-500 
                          transition-colors mb-2 line-clamp-2">
                          {product.name}
                        </h3>
                      </Link>

                      {/* Product Details */}
                      <div className="space-y-2 mb-3">
                        {product.gsm && <p className="text-sm text-gray-600">{product.gsm} GSM</p>}
                        {product.size && <p className="text-sm text-gray-600">Size: {product.size}</p>}
                        {product.sheets && (
                          <p className="text-sm text-gray-600">{product.sheets} sheets/pack</p>
                        )}
                      </div>

                      {/* Rating */}
                      {product.rating && (
                        <div className="flex items-center mb-3">
                          <div className="flex items-center text-yellow-400">
                            <Star className="h-4 w-4 fill-current" />
                            <span className="ml-1 text-sm font-medium text-gray-900">
                              {product.rating}
                            </span>
                          </div>
                          <span className="mx-2 text-gray-300">•</span>
                          <span className="text-sm text-gray-600">
                            {product.reviewCount || 0} reviews
                          </span>
                        </div>
                      )}

                      {/* Price and Add to Cart */}
                      <div className="flex items-center justify-between">
                        <span className="text-xl font-bold text-gray-900">
                          {formatPrice(product.price)}
                        </span>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleAddToCart(product)}
                            disabled={product.stock === 0}
                            className={`inline-flex items-center px-3 py-2 rounded-lg transition-colors ${
                              product.stock === 0
                                ? 'bg-gray-200 cursor-not-allowed text-gray-500'
                                : 'bg-brand-green-500 hover:bg-brand-green-600 text-black'
                            }`}
                          >
                            <ShoppingCart className="h-5 w-5" />
                          </button>
                          <a
                            href={`https://wa.me/254706788802?text=Hi, I'm interested in ${product.name}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-black transition-colors"
                          >
                            <FaWhatsapp className="h-5 w-5" />
                          </a>
                        </div>
                      </div>

                      {product.stock > 0 && product.stock < 25 && (
                        <p className="text-sm text-yellow-600 mt-2">
                          Only {product.stock} left in stock
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination - To be implemented with Firebase pagination */}
            {sortedProducts.length > 0 && (
              <div className="mt-8 flex justify-center">
                <nav className="flex items-center space-x-2">
                  <button className="px-3 py-2 bg-white border rounded-lg text-gray-500 
                    hover:text-brand-blue-500 transition-colors">
                    Previous
                  </button>
                  <button className="px-3 py-2 bg-brand-blue-500 text-white rounded-lg">1</button>
                  <button className="px-3 py-2 bg-white border rounded-lg text-gray-500 
                    hover:text-brand-blue-500 transition-colors">
                    2
                  </button>
                  <button className="px-3 py-2 bg-white border rounded-lg text-gray-500 
                    hover:text-brand-blue-500 transition-colors">
                    3
                  </button>
                  <button className="px-3 py-2 bg-white border rounded-lg text-gray-500 
                    hover:text-brand-blue-500 transition-colors">
                    Next
                  </button>
                </nav>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Products;